/* eslint-disable react-hooks/exhaustive-deps */
import React, { useMemo } from 'react';
import * as THREE from 'three';

import { const_var, params } from '../../../../../redux/reducers/reducer';
import { extrudeSettings, textureAnisotropy} from '../../../../../utils/Functions';
import { CSG } from '../../../../../assets/libs/csg-lib';
import { useThree } from '@react-three/fiber';
import { controls } from '../../../../../redux/reducers/common/eventReducer';

const WithWindows = ({ textureBody, textureRoof }) => {
    const trimColor = params.p_t_c.replace('0x', '#');
    const wallColor = params.p_w_c.replace('0x', '#');
    const roofColor = params.p_r_c.replace('0x', '#');
      
    const width = 4;
    const length = 4;
    const height = 3;

    const baseModelHeight = height / 5;
    const pillarHeight = height - baseModelHeight;
    const pillarHeightOffset = 0.2;
    const pillarSize = 0.22;
    const initPositionY = width / 2 * (params.p_r_p / 12)
    const railLength = pillarSize / 5 * 4;

    let railDistance = railLength + 0.03;
    let railCount = Math.floor(pillarHeight / (railDistance)) + 1
    railDistance = pillarHeight / railCount

    const { gl } = useThree();

    const baseModel = useMemo(() => {
        const model = new THREE.Shape();
        model.moveTo(-width / 2, -initPositionY);
        model.lineTo(0, 0);
        model.lineTo(width / 2, -initPositionY);
        model.lineTo(width / 2, baseModelHeight);
        model.lineTo(-width / 2, baseModelHeight);

        return model;
    
    }, [params.p_w, params.p_r_p])

    const sideBaseModel = useMemo(() => {
        const model = new THREE.Shape();
        model.moveTo(-width / 2, -initPositionY);
        model.lineTo(width / 2, -initPositionY);
        model.lineTo(width / 2, baseModelHeight);
        model.lineTo(-width / 2, baseModelHeight);

        return model;
    
    }, [params.p_w, params.p_r_p])

    const pillarModel = useMemo(() => {
        let model = [];
        
        const model_f_l = new THREE.Shape();
        model_f_l.moveTo(-width / 2, -length / 2);
        model_f_l.lineTo(-width / 2, -length / 2 + pillarSize);
        model_f_l.lineTo(-width / 2 + pillarSize, -length / 2 + pillarSize);
        model_f_l.lineTo(-width / 2 + pillarSize, -length / 2);
        model_f_l.closePath();

        const model_f_r = new THREE.Shape();
        model_f_r.moveTo(width / 2, -length / 2);
        model_f_r.lineTo(width / 2, -length / 2 + pillarSize);
        model_f_r.lineTo(width / 2 - pillarSize, -length / 2 + pillarSize);
        model_f_r.lineTo(width / 2 - pillarSize, -length / 2);
        model_f_r.closePath();

        const model_b_l = new THREE.Shape();
        model_b_l.moveTo(-width / 2, length / 2);
        model_b_l.lineTo(-width / 2, length / 2 - pillarSize);
        model_b_l.lineTo(-width / 2 + pillarSize, length / 2 - pillarSize);
        model_b_l.lineTo(-width / 2 + pillarSize, length / 2);
        model_b_l.closePath();

        const model_b_r = new THREE.Shape();
        model_b_r.moveTo(width / 2, length / 2);
        model_b_r.lineTo(width / 2, length / 2 - pillarSize);
        model_b_r.lineTo(width / 2 - pillarSize, length / 2 - pillarSize);
        model_b_r.lineTo(width / 2 - pillarSize, length / 2);
        model_b_r.closePath();

        model.push(model_f_l)
        model.push(model_f_r)
        model.push(model_b_l)
        model.push(model_b_r)
        
        return model;
    }, [])

    const outWindowModel = useMemo(() => {
        const model = new THREE.Shape()
        model.moveTo(-width / 2 + pillarSize, 0)
        model.lineTo(-width / 2 + pillarSize, pillarHeight + pillarHeightOffset)
        model.lineTo(width / 2 - pillarSize, pillarHeight + pillarHeightOffset)
        model.lineTo(width / 2 - pillarSize, 0)

        const del = new THREE.Path()
        del.moveTo(-width / 2 + 2 * pillarSize, pillarSize)
        del.lineTo(-width / 2 + 2 * pillarSize, pillarHeight + pillarHeightOffset - 1.5 * pillarSize)
        del.lineTo(width / 2 - 2 * pillarSize, pillarHeight + pillarHeightOffset - 1.5 * pillarSize)
        del.lineTo(width / 2 - 2 * pillarSize, pillarSize)

        model.holes.push(del)

        return model;
    }, [width, pillarHeight])

    const outHiddenWindowModel = useMemo(() => {
        const model = new THREE.Shape()
        model.moveTo(-width / 2 + pillarSize, 0)
        model.lineTo(-width / 2 + pillarSize, pillarHeight + pillarHeightOffset)
        model.lineTo(width / 2 - pillarSize, pillarHeight + pillarHeightOffset)
        model.lineTo(width / 2 - pillarSize, 0)

        return model;
    }, [width, pillarHeight])

    const shortBar = useMemo(() => {
        const model_f_l = new THREE.Shape();
        model_f_l.moveTo(-width / 2, -length / 2);
        model_f_l.lineTo(-width / 2, -length / 2 + pillarSize);
        model_f_l.lineTo(-width / 2 + 0.3 * pillarSize, -length / 2 + pillarSize);
        model_f_l.lineTo(-width / 2 + 0.3 * pillarSize, -length / 2);
        model_f_l.closePath();

        return model_f_l;
    }, [width, length])

    const sideBar = useMemo(() => {
        const model_b_r = new THREE.Shape();
        model_b_r.moveTo(width / 2, length / 2);
        model_b_r.lineTo(width / 2, length / 2 - 0.4 * pillarSize);
        model_b_r.lineTo(width / 2 - pillarSize, length / 2 - 0.4 * pillarSize);
        model_b_r.lineTo(width / 2 - pillarSize, length / 2);
        model_b_r.closePath();


        return model_b_r;
    }, [width, length])

    const roofModel = useMemo(() => {
        const meshWallModel = new THREE.Mesh(new THREE.BoxGeometry(5, 5, 10), new THREE.MeshStandardMaterial({ color: 'green', side:THREE.DoubleSide }));
        const meshDelModel = new THREE.Mesh(new THREE.CylinderGeometry(8, 8, 10, 100), new THREE.MeshStandardMaterial({ color: 'gray', side:THREE.DoubleSide }));
        const meshDelModel1 = new THREE.Mesh(new THREE.CylinderGeometry(8, 8, 10, 100), new THREE.MeshStandardMaterial({ color: 'gray', side:THREE.DoubleSide }));
        const meshDelModel2 = new THREE.Mesh(new THREE.CylinderGeometry(8, 8, 10, 100), new THREE.MeshStandardMaterial({ color: 'gray', side:THREE.DoubleSide }));
        const meshDelModel3 = new THREE.Mesh(new THREE.CylinderGeometry(8, 8, 10, 100), new THREE.MeshStandardMaterial({ color: 'gray', side:THREE.DoubleSide }));

        const meshWallModel_Temp = meshWallModel.clone();
        meshWallModel_Temp.position.set(0, 2.5, 0)
        meshWallModel_Temp.updateMatrixWorld();
        
        const meshDelModel_Temp = meshDelModel.clone();
        meshDelModel_Temp.rotateX(Math.PI / 2)
        meshDelModel_Temp.position.set(6.37, 7, 0)
        meshDelModel_Temp.updateMatrixWorld();

        const meshDelModel1_Temp = meshDelModel1.clone();
        meshDelModel1_Temp.rotateX(Math.PI / 2)
        meshDelModel1_Temp.position.set(-6.37, 7, 0)
        meshDelModel1_Temp.updateMatrixWorld();

        const meshDelModel2_Temp = meshDelModel2.clone();
        meshDelModel2_Temp.rotateZ(Math.PI / 2)
        meshDelModel2_Temp.position.set(0, 7, 6.37)
        meshDelModel2_Temp.updateMatrixWorld();

        const meshDelModel3_Temp = meshDelModel3.clone();
        meshDelModel3_Temp.rotateZ(Math.PI / 2)
        meshDelModel3_Temp.position.set(0, 7, -6.37)
        meshDelModel3_Temp.updateMatrixWorld();

        const meshWallModel_CSG = CSG.fromMesh(meshWallModel_Temp)
        const meshDelModel_CSG = CSG.fromMesh(meshDelModel_Temp)
        const meshDelModel1_CSG = CSG.fromMesh(meshDelModel1_Temp)
        const meshDelModel2_CSG = CSG.fromMesh(meshDelModel2_Temp)
        const meshDelModel3_CSG = CSG.fromMesh(meshDelModel3_Temp)

        const wallModel_CSG = meshWallModel_CSG.subtract(meshDelModel_CSG)
        const wallModel1_CSG = wallModel_CSG.subtract(meshDelModel1_CSG)
        const wallModel2_CSG = wallModel1_CSG.subtract(meshDelModel2_CSG)
        const wallModel3_CSG = wallModel2_CSG.subtract(meshDelModel3_CSG)

        const CSGWallModel = CSG.toMesh(
            wallModel3_CSG,
            new THREE.Matrix4(),
            new THREE.MeshStandardMaterial({ color: 'green', side:THREE.DoubleSide })
        )

        const CSGWallModel_Temp = CSGWallModel.clone()
        CSGWallModel_Temp.position.set(0, -0.1, 0)
        CSGWallModel_Temp.updateMatrixWorld()

        const WallModel1_CSG = CSG.fromMesh(CSGWallModel)
        const CSGWallModel_CSG = CSG.fromMesh(CSGWallModel_Temp)

        const WallModel_CSG = WallModel1_CSG.subtract(CSGWallModel_CSG)

        const CSGWallModel1 = CSG.toMesh(
            WallModel_CSG,
            new THREE.Matrix4(),
            new THREE.MeshStandardMaterial({ color: 'green', side:THREE.DoubleSide })
        )

        const CSGWallModel_Temp1 = CSGWallModel1.clone();

        const DelModel = meshWallModel.clone()
        DelModel.rotateY(Math.PI / 4)
        DelModel.position.set(1.03, 0, -2.5)
        DelModel.updateMatrixWorld()

        const DelModel1 = meshWallModel.clone()
        DelModel1.rotateY(-Math.PI / 4)
        DelModel1.position.set(-1, 0, 4.53)
        DelModel1.updateMatrixWorld()

        const CSGWallModel1_CSG = CSG.fromMesh(CSGWallModel_Temp1)
        const DelModel_CSG = CSG.fromMesh(DelModel)
        const DelModel1_CSG = CSG.fromMesh(DelModel1)

        const WallModel2_CSG = CSGWallModel1_CSG.subtract(DelModel_CSG)
        const WallModel3_CSG = WallModel2_CSG.subtract(DelModel1_CSG)

        const CSGWallModel2 = CSG.toMesh(
            WallModel3_CSG,
            new THREE.Matrix4(),
            new THREE.MeshStandardMaterial({ color: 'green', side:THREE.DoubleSide })
        )

        return CSGWallModel2;
    }, [])
    

    return (
        <group position={[0, 0.005, 0]}>
            {new Array(2).fill("").map((_, index) =>
                <mesh name='front-base-model' position={[0, 0, length * index - length / 2]}>
                    <extrudeGeometry args={[baseModel, extrudeSettings(0.01)]} />
                    <meshStandardMaterial map={textureBody} bumpMap={textureBody} bumpScale={0.8} side={THREE.DoubleSide} color={wallColor} emissive={'#242424'} roughness={0.5} />
                </mesh>
            )}
            {new Array(2).fill("").map((_, index) =>
                <mesh name='side-base-model' position={[length * index - length / 2, 0, 0]} rotation={[0, Math.PI / 2, 0]}>
                    <extrudeGeometry args={[sideBaseModel, extrudeSettings(0.01)]} />
                    <meshStandardMaterial map={textureBody} bumpMap={textureBody} bumpScale={0.8} side={THREE.DoubleSide} color={wallColor} emissive={'#242424'} roughness={0.5} />
                </mesh>
            )}

            <mesh name='border-plane' position={[0, baseModelHeight, 0]} rotation={[-Math.PI / 2, 0, 0]}>
                <boxGeometry args={[width + 0.3, length + 0.3, 0.15]} />
                <meshStandardMaterial map={textureBody} bumpMap={textureBody} bumpScale={0.8} side={THREE.DoubleSide} color={wallColor} emissive={'#242424'} roughness={0.5} />
            </mesh>
            <mesh name='pillar-model' position={[0, baseModelHeight, 0]} rotation={[-Math.PI / 2, 0, 0]}>
                <extrudeGeometry args={[pillarModel, extrudeSettings(pillarHeight + pillarHeightOffset)]} />
                <meshStandardMaterial map={textureBody} bumpMap={textureBody} bumpScale={0.8} side={THREE.DoubleSide} color={wallColor} emissive={'#242424'} roughness={0.5} />
            </mesh>

            {new Array(4).fill("").map((_, index) => 
                <group key={`cupola-roof-model-${index}`} name='roof-model' position={[0, height, 0]} rotation={[0, Math.PI / 2 * index, 0]} >
                    <mesh geometry={roofModel.geometry} position={[0.006, 0, 0]} scale={[1, 1, 1]}>
                    {/* <meshStandardMaterial map={textureRoof} bumpMap={textureRoof} bumpScale={0.8} side={THREE.DoubleSide} color={roofColor} emissive={'#242424'} roughness={0.5} metalness={0.1} /> */}
                    <meshStandardMaterial onUpdate={(self) => {self.needsUpdate = true}} map={textureRoof}  color={roofColor} side={THREE.DoubleSide} roughness={1} metalness={0.35} toneMapped={false} emissive={'#101010'} transparent={params.is_translusant || (controls.isCheckActiveTab == "doorwindow" && const_var.focusedSection !== 'center')} opacity={0.02} />
                    </mesh>
                </group>
            )}
            
            <>
                {new Array(2).fill("").map((_, index) => 
                    <group name='outWindow-model' position={[0, baseModelHeight, 0]} >
                        <mesh  name='cupola-outWindow-model' position={[0, 0, -width * index + width / 2]}>
                            <extrudeGeometry args={[outWindowModel, extrudeSettings(-pillarSize * (1 - 2 * index))]}/>
                            <meshStandardMaterial side={THREE.DoubleSide} color={trimColor} roughness={0.5} metalness={0.2} toneMapped={false} />
                        </mesh>
                        <mesh  name='cupola-outWindow-model' position={[0, 0, -width * index + width / 2]}>
                            <extrudeGeometry args={[outHiddenWindowModel, extrudeSettings(-pillarSize * (1 - 2 * index))]}/>
                            <meshStandardMaterial color={trimColor}  side={THREE.DoubleSide} opacity={0.001} transparent/>
                        </mesh>
                    </group>
                )}
                {new Array(2).fill("").map((_, index) => 
                    <group name='window-model' position={[0, baseModelHeight, 0]} rotation={[0, Math.PI / 2, 0]}>
                        <mesh name='cupola-window-model' position={[0, 0, -width * index + width / 2]}>
                            <extrudeGeometry args={[outWindowModel, extrudeSettings(-pillarSize * (1 - 2 * index))]}/>
                            <meshStandardMaterial side={THREE.DoubleSide} color={trimColor} roughness={0.5} metalness={0.2} toneMapped={false} />
                        </mesh>
                        <mesh name='cupola-window-model' position={[0, 0, -width * index + width / 2]}>
                            <extrudeGeometry args={[outHiddenWindowModel, extrudeSettings(-pillarSize * (1 - 2 * index))]}/>
                            <meshStandardMaterial color={trimColor} side={THREE.DoubleSide} opacity={0.001} transparent/>
                        </mesh>
                    </group>
                )}
            </>
            <>
                <>
                    {new Array(2).fill("").map((_, index) => 
                        <group name='shortFrontBar' position={[0, 0, (-width + pillarSize) * index]}>
                            <mesh position={[width / 2, baseModelHeight + pillarSize,0]} rotation={[-Math.PI / 2, 0, 0]}>
                                <extrudeGeometry args={[shortBar, extrudeSettings(pillarHeight - 1.75 * pillarHeightOffset)]} />
                                <meshStandardMaterial  color={trimColor} roughness={0.5} metalness={0.2} side={THREE.DoubleSide} />
                            </mesh>
                        </group>
                    )}
                        {new Array(2).fill("").map((_, index) => 
                        <group name='shortFrontBar' position={[(-width + pillarSize) * index, 0, 0]} rotation={[0, Math.PI / 2, 0, ]}>
                            <mesh position={[width / 2, baseModelHeight + pillarSize,0]} rotation={[-Math.PI / 2, 0, 0]}>
                                <extrudeGeometry args={[shortBar, extrudeSettings(pillarHeight - 1.75 * pillarHeightOffset)]} />
                                <meshStandardMaterial  color={trimColor} roughness={0.5} metalness={0.2} side={THREE.DoubleSide} />
                            </mesh>
                        </group>
                    )}
                </>
                <>
                    {new Array(2).fill("").map((_, index) => 
                        <mesh name='sideBar' position={[0, 0, (width / 2 - 2 * pillarSize) * (2 * index - 1)]} rotation={[0, Math.PI * index, 0]}>
                            <extrudeGeometry args={[sideBar, extrudeSettings(width - 4 * pillarSize)]} />
                            <meshStandardMaterial  color={trimColor} roughness={0.5} metalness={0.2} side={THREE.DoubleSide} />
                        </mesh>
                    )}
                    {new Array(2).fill("").map((_, index) => 
                        <group>
                            <mesh name='sideBar' position={[ - (width / 2 - 2 * pillarSize), 0, (width - pillarSize) * index]} rotation={[0, Math.PI / 2, 0]}>
                                <extrudeGeometry args={[sideBar, extrudeSettings(width - 4 * pillarSize)]} />
                                <meshStandardMaterial  color={trimColor} roughness={0.5} metalness={0.2} side={THREE.DoubleSide} />
                            </mesh>
                        </group>
                    )}
                </>
            </>
        </group>
    );
}

export default WithWindows;
